'use client'

import { Language as LanguageIcon, WarningAmber } from '@mui/icons-material'
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
  useTheme
} from '@mui/material'
import { NextIntlClientProvider, useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import ThemeComponent from '@core/theme/ThemeComponent'

import Center from '~/components/Center'
import EmailSupport from '~/components/EmailSupport'
import { LoadingMessage } from '~/components/Loaders'
import LoadingButton from '~/components/LoadingButton'
import TextFormField from '~/components/forms/TextField'
import envConfig from '~/configs/env'
import { useGetAuthorizationTenantQuery } from '~/data/api/client/public'
import type { PreferredLanguage } from '~/data/types'
import useTranslationMessages from '~/hooks/useTranslationMessages'
import { useLocale, usePreferredLanguage, useSetPreferredLanguage } from '~/store/slices/display'
import { useSetTenantInfo } from '~/store/slices/tenant'
import { useMediaQuery } from '~/theme/hooks'
import { day, night } from '~/theme/palettes'

import getCustomerAssets from '../assets'

const defaultLogoUrl = '/images/logos/synop-logo-600-alpha.png'
const defaultLogoUrlOnDark = '/images/logos/synop-logo-light-ink.svg'

// Treat any hex color with perceived luminance below this threshold as "dark".
// A tenant's `widgetBackground` is fixed regardless of the viewer's theme, so the
// card's palette follows that color rather than the user's saved preference.
const DARK_LUMINANCE_THRESHOLD = 0.5

function isDarkHexColor(hex: string | undefined): boolean {
  if (!hex) return false
  const cleaned = hex.trim().replace(/^#/, '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned
  if (full.length !== 6) return false
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  if ([r, g, b].some((v) => Number.isNaN(v))) return false
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance < DARK_LUMINANCE_THRESHOLD
}

export default function TenantDiscoveryWrapper({ onTenantSet }: TenantDiscoveryProps) {
  const preferredLanguage = usePreferredLanguage()
  const locale = useLocale()
  const translationMessages = useTranslationMessages(preferredLanguage, { blank: false, randomize: false })

  return (
    <ThemeComponent>
      {translationMessages ? (
        <NextIntlClientProvider locale={locale ?? envConfig.defaultLocale} messages={translationMessages}>
          <TenantDiscovery onTenantSet={onTenantSet} />
        </NextIntlClientProvider>
      ) : (
        <LoadingMessage />
      )}
    </ThemeComponent>
  )
}

interface TenantDiscoveryProps {
  onTenantSet: () => void
}

type FormValues = {
  email: string
}

export function TenantDiscovery({ onTenantSet }: TenantDiscoveryProps) {
  const t = useTranslations('auth.TenantDiscovery')
  const isSmUp = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const customerAssetsByUrl = getCustomerAssets()
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null)
  const setTenantInfo = useSetTenantInfo()
  const preferredLanguage = usePreferredLanguage()
  const setPreferredLanguage = useSetPreferredLanguage()
  const emailInputRef = useRef<HTMLInputElement>(null)
  const outerTheme = useTheme()

  // Without a tenant widget color the card follows the viewer's theme. With one,
  // the card is painted that color, so its palette (text, dividers, input borders,
  // focus states) is forced to `day` or `night` to match it. Spacing, typography,
  // shape, and component overrides are inherited from `outerTheme`.
  const widgetBackground = customerAssetsByUrl?.widgetBackground
  const cardTheme = useMemo(() => {
    if (!widgetBackground) return outerTheme
    const palette = isDarkHexColor(widgetBackground) ? night : day
    return createTheme(outerTheme, {
      palette: {
        ...palette,
        background: {
          ...palette.background,
          paper: widgetBackground
        }
      }
    })
  }, [outerTheme, widgetBackground])
  const isCardDark = cardTheme.palette.mode === 'dark'

  const methods = useForm<FormValues>({ mode: 'all' })
  const { handleSubmit, formState } = methods

  const searchParams = useSearchParams()
  const errorParam = searchParams?.get('error')

  const { data, isLoading } = useGetAuthorizationTenantQuery({ email: submittedEmail ?? '' }, { skip: !submittedEmail })

  // Store tenant info in redux when received
  useEffect(() => {
    // We will eventually also be including organization from tenant disco so only
    // want to run when data is returned.
    if (data) {
      const tenant = customerAssetsByUrl?.auth0Tenant ?? data.tenant
      setTenantInfo({ tenant, email: submittedEmail! })
      onTenantSet()
    }
  }, [data, customerAssetsByUrl, setTenantInfo, onTenantSet, submittedEmail])

  function onSubmit(values: FormValues) {
    // Matches [anything]@[anything].[anything]
    if (!/^.+@.+\..+$/.test(values.email)) {
      methods.setError('email', { type: 'manual', message: t('errors_emailInvalid') })
      emailInputRef.current?.focus()
      return
    }
    setSubmittedEmail(values.email)
  }

  // The host names the tenant, and the stored connection sends Auth0 straight to Uber instead of the password form.
  function onSsoSelect() {
    if (!customerAssetsByUrl?.ssoButton) return
    setTenantInfo({
      tenant: customerAssetsByUrl.auth0Tenant,
      email: '',
      connection: customerAssetsByUrl.ssoButton.connection
    })
    onTenantSet()
  }

  return (
    <Center.Middle
      height="auto"
      sx={{
        minHeight: '100vh',
        background: customerAssetsByUrl?.backgroundColor ?? 'background.default',
        backgroundImage: customerAssetsByUrl?.backgroundImage && `url(${customerAssetsByUrl?.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <ThemeProvider theme={cardTheme}>
        <Paper
          elevation={6}
          sx={{
            p: 0,
            borderRadius: '16px',
            width: isSmUp ? '400px' : '100vw',
            minHeight: isSmUp ? '540px' : '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxShadow: isCardDark ? '0 8px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(0,0,0,0.08)',
            border: isCardDark ? 1 : 0,
            borderColor: 'divider',
            px: 2,
            backgroundColor: 'background.paper',
            backgroundImage: 'none',
            position: 'relative'
          }}
        >
        <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
          <FormControl size="small">
            <Select
              value={preferredLanguage}
              onChange={(e) => setPreferredLanguage(e.target.value as PreferredLanguage)}
              input={
                <OutlinedInput
                  startAdornment={
                    <InputAdornment position="start">
                      <LanguageIcon fontSize="small" />
                    </InputAdornment>
                  }
                />
              }
            >
              <MenuItem value="en">{t('languages_English')}</MenuItem>
              <MenuItem value="fr">{t('languages_French')}</MenuItem>
              <MenuItem value="de">{t('languages_German')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          px={8}
          gap={4}
          height="100%"
        >
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <Box
              component="img"
              src={customerAssetsByUrl?.logoUrl ?? (isCardDark ? defaultLogoUrlOnDark : defaultLogoUrl)}
              alt={t('logoAlt', { brand: customerAssetsByUrl?.title ?? 'Synop' })}
              style={{
                maxWidth: isSmUp ? '360px' : '90%',
                maxHeight: `${customerAssetsByUrl?.logoMaxHeight ?? 64}px`,
                marginBottom: 24
              }}
            />
            <Typography variant="h5" fontWeight={600} mb={2} align="center">
              {t('title')}
            </Typography>
          </Box>
          {errorParam && (
            <Stack alignItems="center">
              <Stack direction="row" spacing={3} alignItems="center">
                <WarningAmber color="error" />
                <Typography color="error" variant="subtitle1" align="center">
                  {t(denialMessageKey(searchParams?.get('error_description')))}
                </Typography>
              </Stack>
              <Typography color="error" variant="body2" align="center">
                {t.rich('errors_pleaseContactSupportAt', {
                  email: () => <EmailSupport />
                })}
              </Typography>
            </Stack>
          )}
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <TextFormField
                name="email"
                label={t('emailLabel')}
                type="email"
                autoComplete="email"
                disabled={isLoading}
                sx={{
                  mb: 5,
                  borderRadius: '8px',
                  ...(customerAssetsByUrl?.accentColor && {
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: customerAssetsByUrl.accentColor
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': {
                        color: customerAssetsByUrl.accentColor
                      }
                    }
                  })
                }}
                size="medium"
                inputRef={emailInputRef}
              />
              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={isLoading}
                sx={{
                  textTransform: 'none',
                  height: '52px',
                  py: 1,
                  fontSize: '16px',
                  borderRadius: '8px',
                  backgroundColor: `${customerAssetsByUrl?.accentColor ?? 'primary'} !important`,
                  color: customerAssetsByUrl?.contrastButtonText
                }}
                disabled={isLoading || formState.isSubmitting}
              >
                {t('continueButton')}
              </LoadingButton>
            </form>
          </FormProvider>
          {customerAssetsByUrl?.ssoButton?.provider === 'uber' && (
            <Box width="100%">
              <Divider sx={{ my: 4 }}>{t('orDivider')}</Divider>
              <Button
                variant="outlined"
                // The theme colors primary outlined buttons Synop blue; 'inherit' lets the host's accent through.
                color={customerAssetsByUrl?.accentColor ? 'inherit' : 'primary'}
                fullWidth
                onClick={onSsoSelect}
                disabled={isLoading || formState.isSubmitting}
                startIcon={<Box component="img" src="/images/logos/uber-logo.svg" alt="" width={20} height={20} />}
                sx={{
                  textTransform: 'none',
                  height: 52,
                  borderRadius: '8px',
                  fontSize: 16,
                  color: customerAssetsByUrl?.accentColor
                }}
              >
                {t('signInWithUber')}
              </Button>
            </Box>
          )}
        </Box>
        </Paper>
      </ThemeProvider>
    </Center.Middle>
  )
}

/** Only the transient `identity_service_error` gets its own (retry) message; every other reason is generic. */
function denialMessageKey(reason: string | null | undefined) {
  return reason === 'identity_service_error' ? 'errors_denied_identityServiceError' : 'errors_couldNotCompleteLogin'
}
